import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Box, Grid, Typography, CircularProgress, Container, Divider } from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import Reviews from "./Reviews";
import ListImage from "../listdetails/ListImage";
import ServiceDetailCard from "./ServiceDetailCard";
import AboutPolicy from "../listdetails/AboutPolicy";
import config from "../../config";
import axios from "axios";
import { connect } from "react-redux";
import * as Actions from "../../redux/actions";
import ServiceListCard from "./ServiceListCard";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";

const ServicesListingDetailPage = ({ loading, loadServiceListings, serviceListing, jwtToken }) => {
  const navigate = useNavigate();
  const { listId, serviceId } = useParams();
  const [listData, setListData] = useState();
  const [reviews, setReviews] = useState([]);
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef(null);

  const { scrollY } = useScroll();
  const imageParallax = useTransform(scrollY, [0, 500], [0, -30]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [listData]);

  useEffect(() => {
    const getListingData = async (id) => {
      try {
        const res = await axios.get(`${config.SERVER_URL}/api/v1/listing_services/${id}`, {
          headers: { "Content-Type": "application/json" },
        });
        if (res.status === 200) {
          setListData(res.data.data);
          window.scrollTo({ top: 0 });
        }
      } catch (e) {}
    };
    if (listId) getListingData(listId);
  }, [listId]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(
          `${config.SERVER_URL}/api/v1/reviews?listing_service_id=${listId}`,
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` } }
        );
        if (res.status === 200) setReviews(res.data.reviews);
      } catch (e) {}
    };
    if (listData) fetchReviews();
  }, [listData]);

  useEffect(() => {
    if (listData) {
      loadServiceListings({ category_id: listData.service_id });
    }
  }, [listData]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>
      {/* Breadcrumb header */}
      <Box sx={{ borderBottom: "1px solid #1e1e1e", backgroundColor: "#0d0d0d", py: 1.5 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
            <Typography sx={{ color: "#444", fontSize: "13px" }}>/</Typography>
            <Typography component={Link} to="/services" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Services</Typography>
            {listData && (
              <>
                <Typography sx={{ color: "#444", fontSize: "13px" }}>/</Typography>
                <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }} noWrap>{listData.name}</Typography>
              </>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        {!listData ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#FFC001" }} />
          </Box>
        ) : (
          <>
            {/* Main 2-col layout */}
            <Grid container spacing={4} alignItems="flex-start">
              {/* Left: Image gallery */}
              <Grid item xs={12} md={7}>
                <Box ref={sentinelRef} sx={{ height: "1px", mt: "-1px" }} />
                <motion.div
                  key={listId + "-img"}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                >
                  <Box
                    className="gallery-wrapper"
                    sx={{
                      position: { md: "sticky" },
                      top: { md: "80px" },
                      borderRadius: "16px",
                      border: "1px solid",
                      borderColor: isStuck ? "rgba(255,192,1,0.3)" : "#1e1e1e",
                      boxShadow: isStuck ? "0 0 40px rgba(255,192,1,0.08), 0 4px 24px rgba(0,0,0,0.5)" : "none",
                      transition: "border-color 0.5s ease, box-shadow 0.5s ease",
                      overflow: "hidden",
                    }}
                  >
                    <ListImage listData={[...listData.media]} imageParallax={imageParallax} />
                  </Box>
                </motion.div>
              </Grid>

              {/* Right: Info panel */}
              <Grid item xs={12} md={5}>
                <motion.div
                  key={listId + "-info"}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut", delay: 0.1 }}
                >
                  <ServiceDetailCard listData={listData} />
                </motion.div>
              </Grid>
            </Grid>

            {/* Policy */}
            <Box sx={{ mt: 5 }}>
              <Divider sx={{ borderColor: "#222", mb: 4 }} />
              <AboutPolicy listData={listData} />
            </Box>

            {/* Reviews */}
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ borderColor: "#222", mb: 4 }} />
              <Reviews reviews={reviews} listData={listData} reviewType="service" />
            </Box>

            {/* Similar listings */}
            {serviceListing?.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ borderColor: "#222", mb: 4 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <MiscellaneousServicesIcon sx={{ color: "#FFC001", fontSize: 20 }} />
                      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: "20px", md: "26px" } }}>
                        Similar{" "}
                        <Box component="span" sx={{ color: "#FFC001" }}>Services</Box>
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "#666", fontSize: "13px", mt: 0.3 }}>More from this category</Typography>
                  </Box>
                  <Box
                    onClick={() => navigate(`/services/listings/classes/${serviceId}`)}
                    sx={{ cursor: "pointer", backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: "13px", px: 2.5, py: 0.8, borderRadius: "20px", "&:hover": { backgroundColor: "#e6ac00" } }}
                  >
                    See All
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(5, 1fr)" },
                  }}
                >
                  <ServiceListCard listData={serviceListing.slice(0, 5)} />
                </Box>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Listings, Auth }) => ({
  loading: Listings.loading,
  serviceListing: Listings.serviceListing,
  jwtToken: Auth.jwtToken,
});

const mapDispatchToProps = (dispatch) => ({
  loadServiceListings: (params) => dispatch(Actions.thunkLoadServiceListings(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ServicesListingDetailPage);
